/*
 * EComm affiliate attribution -> Shopify cart attributes.
 *
 * Runs in the real storefront page (theme app embed), so it can use the same-origin Ajax Cart API
 * exactly like the legacy script tags (public/js/tracking_app_script.js, direct_link_tas.js) did.
 * Cart attributes become the order's note_attributes on Shopify checkout, and GoKwik preserves them
 * too, so the existing orders/create pipeline (Order_model::IsTrackedOrder / InsertOrderData) needs
 * no change.
 *
 * Storage is shared with the ecomm-tracking-pixel web pixel: the pixel's browser.localStorage is the
 * storefront's own top-frame localStorage, so both write and read the same STORAGE_KEY in the same
 * JSON shape. Either one capturing the landing URL is enough.
 *
 * Served from Shopify's CDN rather than ecomapp.vcommission.com, which ad blockers list.
 */
(function () {
  'use strict';


  if (window.__ecommAttributionLoaded) return;
  window.__ecommAttributionLoaded = true;


  var STORAGE_KEY = 'ecomm_affiliate_attribution';
  var MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000; // matches the pixel's 30-day cookie
  var MAX_VALUE_LENGTH = 1000;


  // Same list as ATTRIBUTION_KEYS in ecomm-tracking-pixel/src/index.js.
  var ATTRIBUTION_KEYS = [
    'tr_click_id', 'click_id',
    'utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'utm_gtm', 'utm_id',
    'campaign_id', 'pub_id',
    'p1', 'source', 'p3', 'p4',
    'fbclid', 'gclid'
  ];


  // Keys this script writes to the cart. click_id/tr_click_id are still captured (they decide what
  // counts as an affiliate landing, same as the pixel) but are never written: the Trackier team owns
  // click ids, and direct_link_tas.js writes a Trackier-SDK-minted tr_click_id this must not touch.
  var NOT_WRITTEN = ['tr_click_id', 'click_id'];
  var CART_KEYS = ATTRIBUTION_KEYS.filter(function (key) { return NOT_WRITTEN.indexOf(key) === -1; })
    .concat(['landing_page_url']);


  var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';


  // Troubleshooting only: run localStorage.setItem('ecomm_attribution_debug', '1') in the storefront
  // console and reload to log every cart write and any attribute another script removes afterwards.
  var DEBUG = false;
  try { DEBUG = window.localStorage.getItem('ecomm_attribution_debug') === '1'; } catch (e) {}
  function debug() {
    if (DEBUG) console.log.apply(console, ['[EComm Attribution]'].concat([].slice.call(arguments)));
  }


  function readUrlAttribution() {
    var result = {};
    try {
      var params = new URLSearchParams(window.location.search);
      ATTRIBUTION_KEYS.forEach(function (key) {
        var value = params.get(key);
        if (value !== null && value !== '') result[key] = value;
      });
    } catch (e) {}
    return result;
  }


  // Same rule as the pixel's hasAffiliateAttribution(), so both decide identically when a landing
  // URL should replace stored attribution.
  function hasAffiliateAttribution(data) {
    if (!data) return false;
    if (data.tr_click_id) return true;
    if (data.utm_source && ['vcommission', 'vcomm'].indexOf(String(data.utm_source).toLowerCase()) !== -1) return true;
    return !!(data.campaign_id || data.pub_id || data.click_id);
  }


  function loadStored() {
    try {
      var raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      var data = JSON.parse(raw);
      if (!data || typeof data !== 'object') return null;
      var captured = Date.parse(data.captured_at || '');
      if (!isNaN(captured) && Date.now() - captured > MAX_AGE_MS) {
        window.localStorage.removeItem(STORAGE_KEY);
        return null;
      }
      return data;
    } catch (e) {
      return null;
    }
  }


  function store(data) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch (e) {}
  }


  function toCartAttributes(data) {
    var attrs = {};
    CART_KEYS.forEach(function (key) {
      if (data[key] !== undefined && data[key] !== null && data[key] !== '') {
        attrs[key] = String(data[key]).slice(0, MAX_VALUE_LENGTH);
      }
    });
    return attrs;
  }


  /*
   * Writes only the attributes that actually differ, so repeat page views cost one GET /cart.js.
   * forceOverwrite (a fresh affiliate landing on this page) lets the newer click replace values
   * already on the cart; otherwise only empty/missing ones are filled. That keeps this from fighting
   * the legacy script tags while both run until 2027-03-01 (direct_link_tas.js writes a Trackier
   * SDK-minted tr_click_id that must not be replaced). Empty values are never sent, because
   * /cart/update.js treats an empty value as "delete this attribute".
   */
  var syncing = false;
  function syncCart(data, forceOverwrite, label) {
    if (syncing || !data) return;
    var wanted = toCartAttributes(data);
    if (Object.keys(wanted).length === 0) return;
    syncing = true;


    fetch(root + 'cart.js', { credentials: 'same-origin', headers: { 'Accept': 'application/json' } })
      .then(function (res) { return res.ok ? res.json() : { attributes: {} }; })
      .then(function (cart) {
        var current = (cart && cart.attributes) || {};
        var update = {};
        Object.keys(wanted).forEach(function (key) {
          var existing = current[key];
          var missing = existing === undefined || existing === null || existing === '';
          if (missing || (forceOverwrite && existing !== wanted[key])) update[key] = wanted[key];
        });
        if (Object.keys(update).length === 0) {
          debug(label, 'cart already in sync', current);
          return;
        }
        debug(label, 'writing', update);
        return fetch(root + 'cart/update.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ attributes: update })
        })
          .then(function (res) { return res.ok ? res.json() : null; })
          .then(function (saved) {
            if (!saved) return debug(label, 'cart/update.js failed');
            var dropped = Object.keys(update).filter(function (key) { return !(saved.attributes || {})[key]; });
            debug(label, 'cart now has', saved.attributes, dropped.length ? 'NOT SAVED: ' + dropped.join(',') : '');
          });
      })
      .catch(function (err) { debug(label, 'error', err); })
      .then(function () { syncing = false; });
  }


  // 1. Capture: a landing URL with affiliate attribution replaces whatever was stored; a URL without
  //    it (direct visit, internal navigation) leaves the stored attribution alone.
  var fromUrl = readUrlAttribution();
  var freshLanding = hasAffiliateAttribution(fromUrl);
  var attribution;
  if (freshLanding) {
    attribution = Object.assign({}, fromUrl, {
      landing_page_url: window.location.href,
      captured_at: new Date().toISOString()
    });
    store(attribution);
  } else {
    attribution = loadStored();
  }
  if (!attribution) return;


  // 2. Persist to the cart now (cart/update.js creates the cart if none exists yet) ...
  syncCart(attribution, freshLanding, 'page-load');


  //    ... and check again once other storefront scripts have run. Another script posting an empty
  //    value for one of our keys (which /cart/update.js treats as delete) would otherwise remove it
  //    for good. Fill-only, so it never overwrites a value someone else set.
  setTimeout(function () { syncCart(loadStored() || attribution, false, 'recheck'); }, 3000);


  // 3. ... and again after the theme changes the cart (AJAX add-to-cart, drawer edits), so a cart
  //    created or replaced after page load still ends up carrying the attributes before the shopper
  //    reaches Shopify checkout or GoKwik. Observes network entries instead of patching fetch/XHR.
  if (typeof PerformanceObserver === 'function') {
    var timer = null;
    try {
      new PerformanceObserver(function (list) {
        var cartChanged = list.getEntries().some(function (entry) {
          return /\/cart\/(add|change|clear)(\.js)?(\?|$)/.test(entry.name);
        });
        if (!cartChanged) return;
        clearTimeout(timer);
        timer = setTimeout(function () { syncCart(loadStored() || attribution, false, 'cart-changed'); }, 300);
      }).observe({ type: 'resource', buffered: false });
    } catch (e) {}
  }
})();



